/* eslint-disable max-len */
import { Badge, Button, styled, Tooltip } from "@mui/material";

import { useTheme } from "@mui/material/styles";
import moment from "moment/moment";
import { useEffect, useState } from "react";
import { generatePath, Link, useSearchParams } from "react-router-dom";
import { ActionBtn, PulseCircle, TableComponent, TableSchema, Text } from "../../components/";
import { AppRoutes, landDetailLink } from "../../config";
import ApiRoutes from "../../config/api.routes";
import { useAxios } from "../../services/http.service";
import { getReadOnlyContract } from "../../services/contract.service";
import { LandToken } from "../../types/LandToken";
import {
  DATE_FORMAT,
  EcoTokenStatus,
  onReassignment,
  onVerification,
  VERIFICATION_INTERVAL_VALUE,
  VERIFICATION_INTERVAL_UNIT,
  TERM_UNIT,
} from "../../utils/constants";
import { sizing } from "../../utils/methods";
import { baseLandsSchema, guardianCol } from "../../utils/table-schemas";
import { formatValue } from "../../utils/formatValue";
import { resolveStandardStatus } from "../../utils/standard-status";
import { TabsRow } from "../EcoBank/TrustWallet";
import { TableWithTabs } from "../EcoBank/UserWallet";
import { ScanButton } from "./Guarding";
import Profile from "./index";
import TrustLands, { isUploadRow } from "./TrustLandsNew";
import { useProfileDataCache } from "../../contexts/ProfileDataCache";
import ReviewLands from "./ReviewLands";
import useAuthorization from "../../contexts/useAuthorization";
import { RespondToChallengeDialog } from "../../components/Challenge";
import useReVerificationGate, { useReVerificationDue } from "../../hooks/useReVerificationGate";

const Verifications = () => {
  const axios = useAxios();
  const { palette } = useTheme();
  const { getCachedData, profileCache } = useProfileDataCache();
  const { user } = useAuthorization();
  const [searchParams] = useSearchParams();

  const [isLoading, setLoading] = useState(true);
  const [tokens, setTokens] = useState<LandToken[]>([]);
  const [unclaimedLands, setUnclaimedLands] = useState<LandToken[]>([]);
  const [activeTab, setActiveTab] = useState<"verified" | "unverified" | "unclaimed" | "review">("verified");

  // Attestation queue — deeds this wallet may independently attest. Fetched here rather than
  // inside ReviewLands so the tab badge and the table read the same rows from one request.
  // Deliberately not routed through ProfileDataCache: eligibility is per-wallet and time-bound
  // (rows drop out as they are attested or as their withhold window closes), so a cached copy
  // would go stale in exactly the way that costs relay gas to discover.
  const [attestLands, setAttestLands] = useState<LandToken[]>([]);
  const [attestLoading, setAttestLoading] = useState(true);

  // Pre-mint review queue — verifications this wallet may challenge before they are minted. Same
  // reasoning as the attestation queue above: per-wallet, time-bound (a row leaves the moment
  // anyone challenges it or the guardian mints), so it is fetched here rather than cached.
  const [reviewLands, setReviewLands] = useState<LandToken[]>([]);
  const [reviewLoading, setReviewLoading] = useState(true);

  // A pre-mint challenge standing against one of THIS verifier's assessments (Core.challenge).
  // The response is Core.respondToChallenge(requestId, evidence) through the relay — one per
  // challenge, so a request answered this session drops its button without waiting on a re-read.
  const [respondTarget, setRespondTarget] = useState<LandToken | null>(null);
  const [respondedRequestIds, setRespondedRequestIds] = useState<string[]>([]);

  useEffect(() => {
    if (!user?.address) {
      return;
    }
    let cancelled = false;
    setAttestLoading(true);
    axios
      .get(ApiRoutes.trust.attestation)
      .then((res) => {
        if (!cancelled) {
          setAttestLands(res.data ?? []);
        }
      })
      .catch((e) => {
        // An inactive wallet (suspended/frozen/dismissed) gets an empty list, not an error, so
        // anything thrown here is a real failure — surface nothing rather than a stale queue.
        console.error("Failed to load attestation queue:", e);
        if (!cancelled) {
          setAttestLands([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setAttestLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.address]);

  useEffect(() => {
    if (!user?.address) {
      return;
    }
    let cancelled = false;
    setReviewLoading(true);
    axios
      .get(ApiRoutes.trust.review)
      .then((res) => {
        if (!cancelled) {
          setReviewLands(res.data ?? []);
        }
      })
      .catch((e) => {
        // A wallet that is not an active verifier gets an empty list, not an error — Core.challenge
        // admits verifiers only, with no holder backstop — so anything thrown here is a real
        // failure. Show nothing rather than a stale queue.
        console.error("Failed to load review queue:", e);
        if (!cancelled) {
          setReviewLands([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setReviewLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.address]);

  const VerificationLandSchema: TableSchema<Required<LandToken>>[] = [
    ...baseLandsSchema,
    guardianCol,
    {
      id: "verifiedAt",
      label: "Previous",
      sx: {
        width: { xs: "75px", md: "150px" },
        minWidth: { xs: "75px", md: "150px" },
        maxWidth: { xs: "75px", md: "150px" },
      },
      headerStyle: {
        width: { xs: "75px", md: "150px" },
        maxWidth: { xs: "75px", md: "150px" },
        minWidth: { xs: "75px", md: "150px" },
      },
      render: (data, _index) => {
        const active = data.etNft?.easementActivated?.contractStart;
        const verifications = data.verifications?.length && data.verifications.length > 1;
        const result = verifications
          ? data.timestamps?.verifiedAt
          : data.etNft?.easementActivated?.contractStart || data.timestamps?.verifiedAt;
        const text = active
          ? verifications
            ? "Re-verified"
            : "Contract started"
          : verifications
          ? "Re-verified"
          : "Verified";
        return (
          <Tooltip
            arrow={true}
            placement="bottom"
            disableHoverListener={!result}
            title={result ? text + " " + moment(result).fromNow() : false}
          >
            <span style={{ cursor: "pointer" }}>
              <Text
                mono
                sx={{
                  fontSize: { xs: 14, md: 16 },
                  textDecoration: "underline",
                  whiteSpace: "nowrap",
                  lineHeight: 1.2,
                  textWrap: "balance",
                  wordBreak: "break-word",
                }}
              >
                {result ? moment(result).format(DATE_FORMAT) : ""}
              </Text>
            </span>
          </Tooltip>
        );
      },
    },
    {
      id: "contact",
      label: "Contact",
      sx: { width: "auto", minWidth: "fit-content", maxWidth: "max-content" },
      render: (data) => (
        <div style={{ display: "flex", flexDirection: "column" }}>
          {data.meta?.additional_contacts?.map((item, index) => (
            <div key={index}>{item}</div>
          ))}
        </div>
      ),
    },
    {
      id: "status",
      label: "Status",
      style: { width: "170px" },
      render: (data) => {
        const lastVrf = data.verifications?.[data.verifications?.length - 1];
        const standardized = resolveStandardStatus(
          {
            status: data.status,
            etNft: data.etNft,
            meta: data.meta,
            offerStatus: data.offerStatus,
          },
          {
            role: "trust",
            lastVerificationStatus: (lastVrf?.status as EcoTokenStatus) || null,
            approvedLabel: "Approved",
          }
        );
        const verification = (lastVrf && onVerification(lastVrf.status)) || onVerification(data.status);
        const isReassignment = (lastVrf && onReassignment(lastVrf.status)) || onReassignment(data.status);
        const reVerification = lastVrf?.status === EcoTokenStatus.RE_VERIFICATION_REQUIRED;
        const claimed = lastVrf?.status === EcoTokenStatus.CLAIMED;
        const verificationStarted =
          lastVrf?.createdAt && moment(lastVrf.createdAt).add(VERIFICATION_INTERVAL_VALUE, VERIFICATION_INTERVAL_UNIT);
        const sold =
          !!data.etNft?.ownerAddress && !!data.etNft?.minterAddress && data.etNft.ownerAddress !== data.minterAddress;
        const minted = data.status === EcoTokenStatus.VERIFIED;
        const approved = data.status === EcoTokenStatus.APPROVED;
        const expired =
          sold && data.etNft?.easementActivated?.contractStart && data.meta?.contractPeriod
            ? moment(data.etNft.easementActivated.contractStart).add(data.meta.contractPeriod, TERM_UNIT).isBefore()
            : false;
        const remainingTrees =
          formatValue(data.etNft?.easementActivated?.treeTotal as any, false) -
          (formatValue(data.etNft?.easementActivated?.rewardClaimed as any, false) || 0);
        const isTreesDepleted = sold && expired && remainingTrees <= 0;
        const textColor = standardized
          ? standardized.color
          : !lastVrf && data.status === EcoTokenStatus.VERIFICATION_REQUIRED
          ? palette.primary.main
          : lastVrf?.status === EcoTokenStatus.DRAFT
          ? palette.error.main
          : lastVrf?.status === EcoTokenStatus.CLAIMED
          ? palette.success.main
          : lastVrf && onVerification(lastVrf.status)
          ? palette.secondary.main
          : lastVrf?.status === EcoTokenStatus.APPROVED
          ? sold
            ? palette.success.main
            : palette.primary.main
          : palette.secondary.main;

        const active =
          minted && data.etNft?.easementActivated?.contractStart
            ? moment(data.etNft.easementActivated.contractStart)
            : null;

        const text = standardized ? (
          <>
            {standardized.showPulse && (
              <PulseCircle
                warning={!!standardized.pulseWarning}
                sold={!!standardized.pulseSold && !standardized.pulseWarning}
              />
            )}
            {standardized.label}
          </>
        ) : isTreesDepleted ? (
          "Expired"
        ) : isReassignment ? (
          "Reassignment"
        ) : verification ? (
          <>
            <PulseCircle />
            {reVerification ? "Required" : claimed ? "Claimed" : "Verification"}
          </>
        ) : sold ? (
          <>
            <PulseCircle sold />
            Active
          </>
        ) : lastVrf?.status === EcoTokenStatus.REJECTED ? (
          "Rejected"
        ) : lastVrf?.status === EcoTokenStatus.VERIFICATION_REQUIRED ? (
          "Uploaded"
        ) : !lastVrf && data.status === EcoTokenStatus.VERIFICATION_REQUIRED ? (
          "Uploaded"
        ) : approved ? (
          "Approved"
        ) : (
          "Verified"
        );

        const single = data.verifications?.length === 1;

        const last =
          minted && (active || sold) && lastVrf?.status === EcoTokenStatus.APPROVED
            ? data.timestamps?.verifiedAt
              ? moment(data.timestamps?.verifiedAt)
              : null
            : null;

        let nextVerification = null;

        if (single) {
          if (active) {
            nextVerification = active.add(VERIFICATION_INTERVAL_VALUE, VERIFICATION_INTERVAL_UNIT);
          } else if (sold) {
            nextVerification = moment(data.timestamps?.verifiedAt).add(
              VERIFICATION_INTERVAL_VALUE,
              VERIFICATION_INTERVAL_UNIT
            );
          }
        } else {
          if (last) {
            nextVerification = last.add(VERIFICATION_INTERVAL_VALUE, VERIFICATION_INTERVAL_UNIT);
          }
        }

        const soon = nextVerification && nextVerification.isBefore(moment().add(3, "h"));
        const pastDue = nextVerification && nextVerification.isBefore(moment());

        if (active) {
          console.log(`Contract started for [${data.id}] at ${moment(active).format(DATE_FORMAT)}`, {
            single,
            status: lastVrf?.status,
            token: data.status,
          });
        } else {
          console.log(`Contract not started for [${data.id}]`, {
            single,
            status: lastVrf?.status,
            token: data.status,
          });
        }

        const tenseText = pastDue ? "due" : "is";

        if (nextVerification) {
          console.log({ expired, reVerification, nextVerification, active });
        }

        return (
          <Tooltip
            arrow={true}
            placement="bottom"
            disableHoverListener={expired || (!reVerification && !nextVerification && !active)}
            title={
              nextVerification
                ? `Next verification ${tenseText} ` +
                  (pastDue ? moment(nextVerification).toNow() : moment(nextVerification).fromNow())
                : verificationStarted
                ? "Should be completed " + verificationStarted.fromNow()
                : false
            }
          >
            <span style={{ cursor: "pointer" }}>
              <Text
                mono
                sx={{ flexDirection: "column", color: pastDue ? "error.main" : soon ? "action.pulse" : "text.primary" }}
              >
                <Text medium contrast capital sx={{ color: textColor, p: 0, display: "flex", alignItems: "center" }}>
                  {text}
                </Text>
                {!expired && !verification && nextVerification && (
                  <Text sx={{ color: "text.disabled" }}>{nextVerification.format(DATE_FORMAT)}</Text>
                )}
                {reVerification && verificationStarted && (
                  <Text sx={{ color: "text.disabled" }}>{verificationStarted.format(DATE_FORMAT)}</Text>
                )}
              </Text>
            </span>
          </Tooltip>
        );
      },
    },
    {
      id: "actions",
      label: "Actions",
      headerStyle: { width: "180px" },
      render: (data) => (
        <VerificationActionsCell
          data={data}
          address={user?.address}
          responded={respondedRequestIds.includes(String(data.requestId))}
          onRespond={setRespondTarget}
        />
      ),
    },
  ];

  const [unclaimedLandsCount, setUnclaimedLandsCount] = useState<number>(0);
  // The Uploads badge must agree with the Uploads table, which filters /trust/tokens down to
  // the actionable rows — so count with the table's own predicate, not the raw list length.
  const countUploadRows = (list: LandToken[] | null | undefined) =>
    (list ?? []).filter((token) => isUploadRow(token, user?.country)).length;
  // const [verifiedLandsCount, setVerifiedLandsCount] = useState<number>(0);
  const [unverifiedLandsCount, setUnverifiedLandsCount] = useState<number>(0);
  const [verifiedTabData, setVerifiedTabData] = useState<LandToken[]>([]);

  // Check for query params and set active tab. `?review` is where the verification page returns a
  // reviewer to after raising (or backing out of) a pre-mint challenge.
  useEffect(() => {
    const uploads = searchParams.get("uploads");
    if (uploads !== null) {
      setActiveTab("unclaimed");
    }
    if (searchParams.get("review") !== null) {
      setActiveTab("review");
    }
  }, [searchParams]);

  useEffect(() => {
    // Wait for cache to finish loading before checking data
    if (profileCache.isLoading) {
      console.log("Verifications: useEffect - Cache is loading, waiting...");
      return;
    }

    // Check if cache already has the data before calling fetchLands
    const cachedTrustTokens = getCachedData("trustTokens");
    const cachedTrustVerifiedLands = getCachedData("trustVerifiedLands");
    const cachedTrustUnverifiedLands = getCachedData("trustUnverifiedLands");

    if (cachedTrustTokens && cachedTrustVerifiedLands && cachedTrustUnverifiedLands) {
      console.log("Verifications: useEffect - Using cached data without API call");
      // Set data directly from cache without calling fetchLands
      console.log("cachedTrustUnverifiedLands:", cachedTrustUnverifiedLands);
      console.log("sortVerifiedLands(cachedTrustVerifiedLands):", sortVerifiedLands(cachedTrustVerifiedLands));
      const combinedData = [...cachedTrustUnverifiedLands, ...sortVerifiedLands(cachedTrustVerifiedLands)];
      console.log("Combined verifiedTabData:", combinedData);
      setVerifiedTabData(combinedData); 
      setUnclaimedLandsCount(countUploadRows(cachedTrustTokens));
      setTokens(cachedTrustVerifiedLands);
      console.log("cachedTrustVerifiedLands:", tokens);
      setUnclaimedLands(cachedTrustUnverifiedLands);
      setUnverifiedLandsCount(cachedTrustUnverifiedLands.length);
      console.log({ unverifiedLandsCount });
      setLoading(false);
    } else {
      console.log("Verifications: useEffect - Cache empty, calling fetchLands");
      fetchLands();
    }
  }, [profileCache.isLoading, profileCache.lastFetched]);

  // const sortLands = (a: LandToken, _b: LandToken) => {
  //   const onReverification = a.verifications?.findIndex((a) => a.status === EcoTokenStatus.RE_VERIFICATION_REQUIRED) !== -1;
  //   if (a.status === "approved") {
  //     return 1;
  //   }
  //   if (a.status === "verification" || a.status === EcoTokenStatus.VERIFICATION_PREPARED) {
  //     return -1;
  //   }
  //   if (onReverification) {
  //     return -1;
  //   }
  //   return -1;
  // };

  const sortVerifiedLands = (lands: LandToken[]) => {
    return [...lands].sort((a, b) => {
      const getStatusPriority = (token: LandToken) => {
        const sold =
          !!token.etNft?.ownerAddress &&
          !!token.etNft?.minterAddress &&
          token.etNft.ownerAddress !== token.minterAddress;
        const expired =
          sold &&
          moment(token.etNft?.easementActivated?.contractStart)
            .add(token.meta?.contractPeriod || 0, TERM_UNIT)
            .isBefore();
        const active = sold && !expired;

        if (expired) return 4; // Expired lands at the bottom
        if (active) return 0;
        if (token.status === EcoTokenStatus.VERIFIED) return 1;
        if (token.status === EcoTokenStatus.APPROVED) return 2;
        return 3; // Other
      };

      return getStatusPriority(a) - getStatusPriority(b);
    });
  };

  const fetchLands = async () => {
    setLoading(true);
    // Reset badge counts when fetching new data
    setUnclaimedLandsCount(0);
    setUnverifiedLandsCount(0);
    try {
      // Try to get cached data first
      const cachedTrustTokens = getCachedData("trustTokens");
      const cachedTrustVerifiedLands = getCachedData("trustVerifiedLands");
      const cachedTrustUnverifiedLands = getCachedData("trustUnverifiedLands");

      if (cachedTrustTokens && cachedTrustVerifiedLands && cachedTrustUnverifiedLands) {
        console.log("Verifications: Using cached trust data", {
          cachedTrustTokens,
          cachedTrustVerifiedLands,
          cachedTrustUnverifiedLands,
        });
        setVerifiedTabData([...cachedTrustUnverifiedLands, ...sortVerifiedLands(cachedTrustVerifiedLands)]);
        setUnclaimedLandsCount(countUploadRows(cachedTrustTokens));
        setTokens(cachedTrustVerifiedLands);
        setUnclaimedLands(cachedTrustUnverifiedLands);
        setUnverifiedLandsCount(cachedTrustUnverifiedLands.length);
      } else {
        console.log("Verifications: Cache miss, fetching from API");
        // Fallback to direct API calls if cache is empty
        const tokenData = await axios.get(ApiRoutes.trust.tokens).then((res) => res.data);
        setUnclaimedLandsCount(countUploadRows(tokenData));
        const response = (await axios
          .get(ApiRoutes.trust.getVerifications + "/?status=" + EcoTokenStatus.APPROVED + "," + EcoTokenStatus.VERIFIED)
          .then((res) => res.data)) as LandToken[];
        setTokens(response);
        // const verified = await axios.get(ApiRoutes.trust.getVerifications + "/?status=approved,minted").then((res) => res.data);
        // setVerifiedLandsCount(verified.length);
        const unverified = await axios
          .get(
            ApiRoutes.trust.getVerifications +
              "/?status=" +
              EcoTokenStatus.RE_VERIFICATION_REQUIRED +
              "," +
              EcoTokenStatus.CLAIMED +
              "," +
              EcoTokenStatus.VERIFICATION_PREPARED
          )
          .then((res) => res.data);
        setUnclaimedLands(unverified);
        setUnverifiedLandsCount(unverified.length);
        setVerifiedTabData([...unverified, ...sortVerifiedLands(response)]);
      }
    } catch (e: any) {
      console.log({ e });
      // Reset badge counts on error
      setUnclaimedLandsCount(0);
      setUnverifiedLandsCount(0);
    } finally {
      setLoading(false);
    }
  };

  // if (!isLoading && tokens.length === 0) {
  //   return (
  //     <Container>
  //       <EmptyBox>
  //         <span>There is nothing here yet!</span>
  //         <span>Check unclaimed lands which require verification</span>
  //         <ScanButton onClick={() => navigator(AppRoutes.Profile.subRoutes.Lands.path)}>Unclaimed lands</ScanButton>
  //       </EmptyBox>
  //     </Container>
  //   );
  // }

  return (
    <>
      <Container>
        {isLoading && (
          <EmptyBox sx={{ borderRadius: "20px", height: "60vh" }}>
            <span>🔎</span>
            <span>Data is loading...</span>
          </EmptyBox>
        )}
        <TableWithTabs sx={{ backgroundColor: "back.table", borderRadius: "20px" }}>
          {!isLoading && (
            <TabsRow sx={{ p: sizing(6), pb: sizing(0), pt: sizing(0), borderRadius: "20px 20px 0px 0px" }}>
              <FilterBlock
                sx={{
                  "@media (min-width: 600px)": {
                    paddingTop: "15px",
                    paddingBottom: "15px",
                    display: "flex",
                    overflowX: "visible",
                    whiteSpace: "nowrap",
                  },
                  "@media (max-width: 599px)": {
                    flexDirection: "row",
                    paddingTop: "10px",
                    width: "100%",
                  },
                }}
              >
                <Badge badgeContent={unverifiedLandsCount || 0} color="error">
                  <ActionBtn
                    className={activeTab === "verified" ? "active" : ""}
                    variant={activeTab === "verified" ? "outlined" : "outlined"}
                    size="medium"
                    disabled={activeTab === "verified"}
                    onClick={() => setActiveTab("verified")}
                  >
                    Verified
                  </ActionBtn>
                </Badge>
                <Badge badgeContent={unclaimedLandsCount || 0} color="error">
                  <ActionBtn
                    className={activeTab === "unclaimed" ? "active" : ""}
                    variant={activeTab === "unclaimed" ? "outlined" : "outlined"}
                    size="medium"
                    disabled={activeTab === "unclaimed"}
                    onClick={() => setActiveTab("unclaimed")}
                  >
                    Uploads
                  </ActionBtn>
                </Badge>
                {/* One tab for both watchdog queues — score review (pre-mint) and deed
                    attestation (post-sale) are the same independent-check duty at two
                    lifecycle stages, so the badge counts both. */}
                <Badge badgeContent={reviewLands.length + attestLands.length} color="error">
                  <ActionBtn
                    className={activeTab === "review" ? "active" : ""}
                    variant={activeTab === "review" ? "outlined" : "outlined"}
                    size="medium"
                    disabled={activeTab === "review"}
                    onClick={() => setActiveTab("review")}
                  >
                    Review
                  </ActionBtn>
                </Badge>
                {/* <Badge badgeContent={unverifiedLandsCount || 0} color="error">
                  <ActionBtn
                    className={activeTab === "unverified" ? "active" : ""}
                    variant={activeTab === "unverified" ? "outlined" : "outlined"}
                    size="medium"
                    disabled={activeTab === "unverified"}
                    onClick={() => setActiveTab("unverified")}
                  >
                    Unverified
                  </ActionBtn>
                </Badge> */}
              </FilterBlock>
            </TabsRow>
          )}
          {activeTab === "verified" && (
            <>
              {!isLoading && verifiedTabData.length === 0 ? (
                <EmptyBox sx={{ borderRadius: "20px", marginTop: "0px", height: "57vh" }}>
                  <span>There is nothing here yet!</span>
                  <span>Check uploaded lands which require verification</span>
                  <ScanButton onClick={() => setActiveTab("unclaimed")}>Uploaded lands</ScanButton>
                </EmptyBox>
              ) : (
                <div style={{ display: isLoading ? "none" : "block", width: "100%" }}>
                  <TableComponent
                    mobileTitle=""
                    schema={VerificationLandSchema}
                    customData={verifiedTabData || []}
                    apiURL=""
                    title={" "}
                    hideTitle={true}
                    totalItems={verifiedTabData.length}
                    setLoading={setLoading}
                    batch={5}
                  />
                </div>
              )}
            </>
          )}

          {activeTab === "unclaimed" && (
            <div style={{ borderRadius: "20px", marginTop: "0px" }}>
              <TrustLands />
            </div>
          )}

          {activeTab === "review" && (
            <div style={{ borderRadius: "20px", marginTop: "0px" }}>
              <ReviewLands
                reviewLands={reviewLands}
                attestLands={attestLands}
                isLoading={reviewLoading || attestLoading}
              />
            </div>
          )}

          {activeTab === "unverified" && (
            <>
              {!isLoading && unclaimedLands.length === 0 ? (
                <EmptyBox sx={{ borderRadius: "20px", marginTop: "0px", height: "57vh" }}>
                  <span>There is nothing here yet!</span>
                  <span>Check unclaimed lands which require verification</span>
                  <ScanButton onClick={() => setActiveTab("unclaimed")}>Uploaded lands</ScanButton>
                </EmptyBox>
              ) : (
                <div style={{ display: isLoading ? "none" : "block", width: "100%" }}>
                  <TableComponent
                    mobileTitle=""
                    schema={VerificationLandSchema}
                    customData={getCachedData("trustUnverifiedLands") || []}
                    apiURL=""
                    title={""}
                    hideTitle={true}
                    totalItems={unclaimedLands.length}
                    setLoading={setLoading}
                    batch={5}
                  />
                </div>
              )}
            </>
          )}
        </TableWithTabs>
      </Container>

      {respondTarget && (
        <RespondToChallengeDialog
          open={!!respondTarget}
          onClose={() => setRespondTarget(null)}
          stage="premint"
          id={respondTarget.requestId}
          challengeEvidence={respondTarget.challengeEvidence}
          challengedBy={respondTarget.challengedBy}
          landName={respondTarget.meta?.name}
          onResponded={() => setRespondedRequestIds((ids) => [...ids, String(respondTarget.requestId)])}
        />
      )}
    </>
  );
};

const Container = ({ children }: any) => <Profile surface="beige">{children}</Profile>;

// Every button in an Actions column takes one fixed height, so a cell that mixes text and
// icon-only buttons lines them up instead of letting each one's content set its size. The
// padding here is inner padding, horizontal only — the height is already fixed.
const TABLE_ACTION_HEIGHT = "36px";

const TableActionButton = styled(Button)({
  height: TABLE_ACTION_HEIGHT,
  minHeight: TABLE_ACTION_HEIGHT,
  padding: "0 16px",
  whiteSpace: "nowrap",
  backgroundColor: "black",
  color: "white",
  fontFamily: "'Inter', sans-serif",
  fontSize: 14,
  fontWeight: 400,
  border: "none",
  "&:hover": {
    backgroundColor: "#333",
    border: "none",
    color: "white",
  },
  "&.Mui-disabled": {
    backgroundColor: "rgba(0, 0, 0, 0.4)",
    color: "rgba(255, 255, 255, 0.6)",
  },
});

export const FilterBlock = styled("div")`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  overflow-x: auto;
  white-space: nowrap;

  /* The strip scrolls horizontally once the tabs outgrow the bar. The Figma has
     no scrollbar, and a classic one both contradicts it and eats 3px of the
     72px bar; scrolling itself (swipe, drag, keyboard focus) is unaffected. */
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }

  @media (max-width: 600px) {
    flex-direction: row;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding-bottom: 8px;
  }
  & button {
    border: 1px solid
      ${({ theme }) => (theme.palette.mode === "dark" ? theme.palette.border.default : theme.palette.border.default)};
    border-radius: 15px;
    text-transform: capitalize;
    padding: 6px 16px;
    transition: none;
    box-sizing: border-box;

    @media (max-width: 600px) {
      /* Holds the Figma's 42px pill (and a comfortable tap target) at the
         smaller mobile type size — 4px of padding left them only 34px tall. */
      padding: 8px 14px;
      font-size: 0.875rem;
    }

    &:hover {
      animation: none;
      transform: none;
      transition: none;
      border: 1px solid
        ${({ theme }) => (theme.palette.mode === "dark" ? theme.palette.border.default : theme.palette.border.default)};
      width: auto;
      height: auto;
      box-sizing: border-box;
    }

    &.active {
      border: 1px solid transparent;
    }

    &:disabled {
      background-color: ${({ theme }) => theme.palette.border.default};
      color: ${({ theme }) => theme.palette.text.primary};
      pointer-events: none;
    }
  }
`;

export const EmptyBox = styled("div")`
  background: #d1efff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  width: 100%;
  height: 330px;
  padding: 0 12px;
  user-select: none;

  span {
    margin-bottom: 15px;
    font-size: 26px;

    &:nth-of-type(1) {
      font-size: 26px;
    }
    &:nth-of-type(3) {
      font-size: 16px;
      color: black;
    }

    @media (max-width: 560px) {
      font-size: 14px;
      &:nth-of-type(3) {
        font-size: 14px;
      }
    }
  }
`;

/**
 * Whether `address` is the verifier behind a challenged, unminted assessment — the only caller
 * Core.respondToChallenge accepts (anyone else reverts NotEasementVerifier). The Verified list is
 * already filtered to trustId = caller, but acceptedBy (the chain's binding) is what the contract
 * checks, and the trust relation is null for a verifier with no User row — so compare explicitly.
 */
const isOwnChallengedAssessment = (token: LandToken, address?: string | null): boolean => {
  const verifier = token.acceptedBy || token.trust?.address;
  return (
    !!token.challengedAt &&
    !!token.requestId &&
    token.status !== EcoTokenStatus.VERIFIED &&
    !!address &&
    !!verifier &&
    verifier.toLowerCase() === address.toLowerCase()
  );
};

/**
 * "Verify in 4h 54m" on a held re-verification button. Minute precision on a 30 s tick — enough to
 * plan around, and the label does not jitter every second. Its own component so only the label
 * re-renders; the cell's useReVerificationDue flips the button back to Verify when the time comes.
 */
const VerifyOpensIn = ({ dueAtMs }: { dueAtMs: number }) => {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(timer);
  }, []);

  const minutes = Math.max(1, Math.ceil((dueAtMs - now) / 60_000));
  const days = Math.floor(minutes / 1440);
  const hours = Math.floor((minutes % 1440) / 60);
  const mins = minutes % 60;
  const span = days > 0 ? `${days}d ${hours}h` : hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  return <>Verify in {span}</>;
};

function VerificationActionsCell({
  data,
  address,
  responded,
  onRespond,
}: {
  data: any;
  address?: string | null;
  // Answered in this session — the chain read below may not have caught up yet.
  responded: boolean;
  onRespond: (token: LandToken) => void;
}) {
  const ownChallenge = isOwnChallengedAssessment(data, address);

  // One response per challenge (AlreadyResponded), and the backend does not record it, so ask
  // Core.challengeResponseOf. A failed read falls back to offering the button: the contract is
  // the guard, and hiding the only way to answer on an RPC hiccup is the worse failure.
  const [respondedOnChain, setRespondedOnChain] = useState<boolean | null>(null);

  useEffect(() => {
    if (!ownChallenge) {
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const coreContract = getReadOnlyContract("ecofutures-core");
        if (!coreContract) {
          throw new Error("Core contract not found");
        }
        const response: string = await coreContract.challengeResponseOf(data.requestId);
        if (!cancelled) {
          setRespondedOnChain(!!response && response.length > 0);
        }
      } catch (e) {
        console.error("[Verifications] Failed to read challengeResponseOf:", e);
        if (!cancelled) {
          setRespondedOnChain(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [data.requestId, ownChallenge]);

  const canRespond = ownChallenge && !responded && respondedOnChain === false;

  let reVerification = false;
  let verification = false;
  if (data.status === EcoTokenStatus.VERIFIED) {
    if (data.verifications?.length) {
      reVerification =
        data.verifications.findIndex(
          (v: any) => v.status === EcoTokenStatus.RE_VERIFICATION_REQUIRED
        ) !== -1 ||
        data.verifications.findIndex(
          (v: any) => v.status === EcoTokenStatus.VERIFICATION_PREPARED
        ) !== -1;
      verification = reVerification;
    }
  } else {
    verification = data.status !== EcoTokenStatus.APPROVED && data.status !== EcoTokenStatus.DRAFT;
  }

  const isExpired =
    data.etNft?.easementActivated?.contractStart &&
    moment(data.etNft.easementActivated.contractStart).add(data.meta.contractPeriod, TERM_UNIT).isBefore();
  const transferPending = data.etNft?.pendingGuardianUpdate === true;

  const isSold =
    !!data.etNft?.ownerAddress &&
    !!data.etNft?.minterAddress &&
    data.etNft.ownerAddress !== data.minterAddress;

  const [onChainPending, setOnChainPending] = useState<boolean | null>(null);

  useEffect(() => {
    if (!isSold || !data.etNft?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const coreContract = getReadOnlyContract("ecofutures-core");
        if (!coreContract) return;
        const result = await coreContract.getEasement(data.etNft.id);
        const { totalVerifications, verificationsPerformed } = result.toObject();
        if (!cancelled) {
          setOnChainPending(Number(verificationsPerformed) < Number(totalVerifications));
        }
      } catch (e) {
        console.error("[Verifications] Failed to fetch on-chain easement data:", e);
      }
    })();
    return () => { cancelled = true; };
  }, [data.etNft?.id, isSold]);

  // For re-verification on sold lands: both off-chain status and on-chain pending must be true.
  // For first-time verifications (no easement yet): off-chain flag is sufficient.
  const needsVerification = (reVerification && onChainPending === true) || (!reVerification && verification);

  // The backend's RE_VERIFICATION_REQUIRED is not "Verify will go through". The flag comes from
  // EcoLens.getAllEasementsToBeVerified, which counts elapsed intervals against
  // verificationsPerformed; verifyEasement's ClaimFundNow gates on releasedCount instead. One
  // catch-up verification releases every missed instalment at once, and from then on EcoLens calls
  // the covenant due for the rest of the term while the contract refuses Verify (CannotClaimYet)
  // until the next interval boundary. So the row runs the verification page's own gate
  // (useReVerificationGate) and holds the button back, saying when it opens. A failed read leaves
  // the button as it was — the page gates again, and the contract is the final guard.
  const onReVerificationRow = data.status === EcoTokenStatus.RE_VERIFICATION_REQUIRED || reVerification;
  const reVerifyGate = useReVerificationGate(onReVerificationRow && needsVerification ? data.etNft?.id : null);
  const { dueAtMs: reVerifyDueAtMs, blocked: reVerifyBlocked } = useReVerificationDue(reVerifyGate);
  const verifyHeld = needsVerification && reVerifyBlocked;

  return (
    <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
      {transferPending && (
        <Link
          to={generatePath(AppRoutes.GuardianTransfer.path, { tokenId: data.id })}
          style={{ display: "flex", textDecoration: "none" }}
        >
          <TableActionButton
            size="small"
            color="primary"
            variant="contained"
            sx={{ textTransform: "none", "&:hover": { border: "none", outline: "none" } }}
          >
            Pending Transfer
          </TableActionButton>
        </Link>
      )}
      {/* Replaces Details rather than sitting beside it: the column cannot fit both, and the
          row's name and thumbnail already link to the land. */}
      {canRespond ? (
        <TableActionButton
          size="small"
          variant="contained"
          onClick={() => onRespond(data)}
          sx={{ textTransform: "none", "&:hover": { border: "none", outline: "none" } }}
        >
          Respond to challenge
        </TableActionButton>
      ) : verifyHeld ? (
        // Disabled buttons swallow hover, so the tooltip hangs off a wrapper.
        <Tooltip
          arrow
          placement="bottom"
          title={
            reVerifyGate?.openChallenge
              ? "A challenge is open on this covenant. Verification waits until it is decided."
              : reVerifyDueAtMs !== null
              ? `Verification opens when the next instalment falls due, ${new Date(reVerifyDueAtMs).toLocaleString()}. Verifying earlier would be refused.`
              : ""
          }
        >
          <span style={{ display: "flex" }}>
            <TableActionButton size="small" variant="contained" disabled sx={{ textTransform: "none" }}>
              {!reVerifyGate?.openChallenge && reVerifyDueAtMs !== null ? (
                <VerifyOpensIn dueAtMs={reVerifyDueAtMs} />
              ) : (
                "Verify"
              )}
            </TableActionButton>
          </span>
        </Tooltip>
      ) : (
        <Link
          state={{ ref: AppRoutes.Profile.subRoutes.Verification.path, canEdit: needsVerification }}
          to={needsVerification ? generatePath(AppRoutes.Verification.path, { tokenId: data.id }) : landDetailLink(data.id)}
          style={{ display: "flex", textDecoration: "none" }}
        >
          <TableActionButton
            size="small"
            variant="contained"
            color="primary"
            sx={{ textTransform: "none", "&:hover": { border: "none", outline: "none" } }}
          >
            {needsVerification ? "Verify" : isExpired ? "Details" : "Details"}
          </TableActionButton>
        </Link>
      )}
    </div>
  );
}

export default Verifications;
